import React from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { environmentContextState, smsPhoneStatusState } from '../../recoil/atoms';
import { isValidEmail } from '../../sizing-tool/utils/email-helper';
import { KlaviyoFormKeys } from '../../models/klaviyo/SubscribeToKlaviyo';
import useEmailSubscribe from '../../hooks/actions/useEmailSubscribe';
import usePhoneSubscribe from '../../hooks/actions/usePhoneSubscribe';
import useSafariBfCacheInvalidator from '../../hooks/useSafariBfCacheInvalidator';
import { useKlaviyoMetrics } from '../../hooks/actions/useKlaviyoMetrics';
import { selectionKlaviyoResponseFamily } from '../../recoil/selectors';
import styles from './LeadGenerationForm.module.scss';
import { Button, ButtonSize } from './buttons/Button';

interface Props {
  klaviyoFormKeys: KlaviyoFormKeys;
  buttonMessage: string | JSX.Element;
  successAction?: () => void;
  isBackInStockSubscription?: boolean;
  productId?: string;
  variantId?: string;
  placeholder?: string;
  usePhoneInput?: boolean;
  placeholderPhone?: string;
  textboxStyle?: React.CSSProperties;
  size?: ButtonSize;
}

// eslint-disable-next-line max-lines-per-function
export const LeadGenerationForm: React.FC<Props> = ({
  klaviyoFormKeys,
  isBackInStockSubscription,
  productId,
  variantId,
  buttonMessage,
  successAction,
  placeholder,
  usePhoneInput,
  placeholderPhone,
  textboxStyle = {},
  size = 'medium',
}) => {
  const environmentContext = useRecoilValue(environmentContextState);
  const emailSubscribe = useEmailSubscribe();
  const [, setKlaviyoStatus] = useRecoilState(selectionKlaviyoResponseFamily(klaviyoFormKeys.formId));
  const phoneSubscribe = usePhoneSubscribe();
  const [klaviyoStatus] = useRecoilState(selectionKlaviyoResponseFamily(klaviyoFormKeys.formId));
  const smsPhoneStatus = useRecoilValue(smsPhoneStatusState);

  const [email, setEmail] = React.useState<string>('');
  const [phone, setPhone] = React.useState<string>('');
  const [subscribe, setSubscribe] = React.useState<boolean>(true);
  const [isKlaviyoError, setIsKlaviyoError] = React.useState<boolean>(false);
  const [isSmsPhoneError, setIsSmsPhoneError] = React.useState<boolean>(false);
  const [isWaiting, setIsWaiting] = React.useState<boolean>(false);
  useSafariBfCacheInvalidator(setIsWaiting);
  const [errorMessage, setErrorMessage] = React.useState<string>();
  const [isDone, setIsDone] = React.useState<boolean>(false);
  const { klaviyoFormViewed, klaviyoFormSubmitted } = useKlaviyoMetrics(klaviyoFormKeys);
  const combinedTextboxStyle: React.CSSProperties = {
    background: 'white',
    border: '1px solid #000000',
    boxSizing: 'border-box',
    padding: '12px',
    display: 'block',
    width: '100%',
    marginBottom: '15px',
    fontSize: '13px',
    letterSpacing: '1px',
    height: 40,
    ...textboxStyle,
  };

  React.useEffect(() => {
    klaviyoFormViewed();
  }, [klaviyoFormViewed]);

  React.useEffect(() => {
    if (klaviyoStatus === undefined) {
      return;
    }

    if (klaviyoStatus?.success) {
      setIsKlaviyoError(false);
      if (successAction) {
        successAction();
      }
      setIsDone(true);
    } else {
      setIsKlaviyoError(true);
    }
  }, [klaviyoStatus, successAction]);

  React.useEffect(() => {
    if (isDone) {
      email && klaviyoFormSubmitted(email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDone]);

  React.useEffect(() => {
    if (smsPhoneStatus === undefined) {
      return;
    }

    if (smsPhoneStatus?.success) {
      if (successAction) {
        successAction();
      }
      setIsDone(true);
    } else {
      setIsSmsPhoneError(true);
    }
  }, [smsPhoneStatus, successAction]);

  return (
    <React.Fragment>
      {!isKlaviyoError && isDone && <div style={{ padding: '20px' }}>Thank you for joining our mailing list!</div>}
      {isKlaviyoError && !isDone && (
        <div key="klaviyo-response-container" className="klaviyo-response-container">
          <span className="hl-klaviyo-response hl-klaviyo-error">Sorry, but we had trouble adding your email. Please try again later.</span>
        </div>
      )}
      {isSmsPhoneError && !isDone && (
        <div key="klaviyo-response-container" className="klaviyo-response-container">
          <span className="hl-klaviyo-response hl-klaviyo-error">Sorry, but we had trouble adding your phone. Please try again later.</span>
        </div>
      )}
      {!isKlaviyoError && !isDone && (
        <React.Fragment>
          <input
            type="email"
            id="email-join-input"
            style={combinedTextboxStyle}
            placeholder={placeholder ? placeholder : 'Email'}
            aria-label={placeholder ? placeholder : 'Email'}
            onChange={(e) => setEmail(e.target.value)}
          />
          {usePhoneInput && (
            <input
              type="tel"
              placeholder={placeholderPhone ? placeholderPhone : 'Phone'}
              aria-label={placeholderPhone ? placeholderPhone : 'Phone'}
              onChange={(e) => setPhone(e.target.value)}
              style={{ ...combinedTextboxStyle, marginTop: 15 }}
              maxLength={12}
              autoComplete="off"
            />
          )}
          {isBackInStockSubscription && (
            <div className={styles.hlKlaviyoCheckboxWrapper}>
              <input id="notify" type="checkbox" className={styles.notifyCheckbox} checked={subscribe} onChange={() => setSubscribe(!subscribe)} />
              <label htmlFor="notify">Subscribe to our newsletter for news &amp; exclusive offers.</label>
            </div>
          )}
          <Button
            text={buttonMessage}
            buttonType="primary"
            size={size}
            onClick={(event) => {
              event.preventDefault();
              if (phone) {
                phoneSubscribe({
                  phone: phone.replace(/\D/g, ''),
                });
                setErrorMessage('');
                setIsWaiting(true);
              }
              if (isValidEmail(email)) {
                emailSubscribe(
                  {
                    isBackInStockSubscription: !!isBackInStockSubscription,
                    klaviyoSiteId: environmentContext.klaviyoSiteId,
                    email,
                    klaviyoListId: klaviyoFormKeys.listId,
                    klaviyoFormId: klaviyoFormKeys.formId,
                    productId,
                    variantId,
                    subscribeForNewsletter: subscribe,
                  },
                  setKlaviyoStatus,
                );
                setErrorMessage('');
                setIsWaiting(true);
              }
              if (!phone && !isValidEmail(email)) {
                setErrorMessage('* Please provide a valid email or phone');
              }
            }}
            disabled={isWaiting}
          />
          {errorMessage && <span className={styles.hlErrorMessage}>{errorMessage}</span>}
          {usePhoneInput && (
            <div className={styles.disclaimer}>
              *I agree to receive recurring automated marketing text messages (e.g. cart reminders) at the phone number provided. Consent is not a condition to
              purchase. Msg &amp; data rates may apply. Msg frequency varies. Reply HELP for help and STOP to cancel. View our&nbsp;
              <a className={styles.link} href="/pages/privacy-policy">
                Privacy Policy
              </a>
              &nbsp;and&nbsp;
              <a className={styles.link} href="/pages/terms-of-service">
                Terms of Service
              </a>
              .
            </div>
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default LeadGenerationForm;
